import type { Draft } from '../../reducers/draft/draft'
import type {
  JobStatus,
  UpgradedJobPostingProduct,
} from '../../reducers/jobs/jobs'
import type { JobPostingProduct } from '../../reducers/entitlement/entitlement'

export const CONFIRM: 'draft/CONFIRM' = 'draft/CONFIRM'
export const CREATE: 'draft/CREATE' = 'draft/CREATE'
export const SET_DUPLICATED_JOB_DRAFT: 'draft/SET_DUPLICATED_JOB_DRAFT' = 'draft/SET_DUPLICATED_JOB_DRAFT'
export const SET_ID: 'draft/SET_ID' = 'draft/SET_ID'
export const SET_DRAFT: 'draft/SET_DRAFT' = 'draft/SET_DRAFT'
export const UPDATE: 'draft/UPDATE' = 'draft/UPDATE'
export const SUBMIT: 'draft/SUBMIT' = 'draft/SUBMIT'
export const EDIT: 'draft/EDIT' = 'draft/EDIT'
export const UPLOAD_LOGO: 'draft/UPLOAD_LOGO' = 'draft/UPLOAD_LOGO'
export const SET_PRODUCT: 'draft/SET_PRODUCT' = 'draft/SET_PRODUCT'
export const SET_UPGRADES: 'draft/SET_UPGRADES' = 'draft/SET_UPGRADES'
export const SET_STATUS: 'draft/SET_STATUS' = 'draft/SET_STATUS'

type ConfirmDraftAction = {
  type: typeof CONFIRM,
}

export type CreateDraftAction = {
  payload: {
    product: JobPostingProduct | null,
  },
  type: typeof CREATE,
}

type SetDraftIdAction = {
  payload: string,
  type: typeof SET_ID,
}

type SetDraftAction = {
  payload: Draft,
  type: typeof SET_DRAFT,
}

export type SetProductOfDraftAction = {
  payload: JobPostingProduct | UpgradedJobPostingProduct,
  type: typeof SET_PRODUCT,
}

export type SetUpgradesOfDraftAction = {
  payload: any,
  type: typeof SET_UPGRADES,
}

export type UpdateDraftAction = {
  payload: {
    draft: Draft,
    id: string,
    status: JobStatus | undefined,
    setSubmitting: (boolean) => void,
  },
  type: typeof UPDATE,
}

export type SubmitDraftAction = {
  payload: {
    id: string,
    product?: JobPostingProduct,
    upgrades?: Array<any>,
  },
  type: typeof SUBMIT,
}

export type EditDraftAction = {
  payload: {
    id: string,
    status: JobStatus | undefined,
    noNavigation?: boolean,
  },
  type: typeof EDIT,
}

export type UploadLogoAction = {
  payload: {
    file: Blob,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    setFieldError: (field: string, errorMsg: string) => void,
  },
  type: typeof UPLOAD_LOGO,
}

export type SetDraftStatusAction = {
  payload: JobStatus,
  type: typeof SET_STATUS,
}

export type SetDuplicatedJobDraftAction = {
  payload: {
    draft: Draft,
    id: string,
    product: JobPostingProduct | UpgradedJobPostingProduct,
    status: JobStatus,
    upgrades: any,
  },
  type: typeof SET_DUPLICATED_JOB_DRAFT,
}

export type DraftActions =
  | ConfirmDraftAction
  | CreateDraftAction
  | EditDraftAction
  | SetDraftAction
  | SetDraftIdAction
  | SetDraftStatusAction
  | SetDuplicatedJobDraftAction
  | SetProductOfDraftAction
  | SetUpgradesOfDraftAction
  | SubmitDraftAction
  | UpdateDraftAction
  | UploadLogoAction

export const confirmDraft = (): ConfirmDraftAction => ({
  type: CONFIRM,
})

export const createDraft = (
  product: CreateDraftAction['payload'],
): CreateDraftAction => ({
  payload: product,
  type: CREATE,
})

export const setDraft = (
  draft: SetDraftAction['payload'],
): SetDraftAction => ({
  payload: draft,
  type: SET_DRAFT,
})

export const setDraftId = (
  id: SetDraftIdAction['payload'],
): SetDraftIdAction => ({
  payload: id,
  type: SET_ID,
})

export const updateDraft = (
  payload: UpdateDraftAction['payload'],
): UpdateDraftAction => ({
  payload,
  type: UPDATE,
})

export const submitDraft = (
  payload: SubmitDraftAction['payload']
): SubmitDraftAction => ({
  payload,
  type: SUBMIT,
})

export const editDraft = (
  payload: EditDraftAction['payload'],
): EditDraftAction => ({
  payload,
  type: EDIT,
})

export const uploadLogo = (
  payload: UploadLogoAction['payload'],
): UploadLogoAction => ({
  payload,
  type: UPLOAD_LOGO,
})

export const setProductOfDraft = (
  product: SetProductOfDraftAction['payload'],
): SetProductOfDraftAction => ({
  payload: product,
  type: SET_PRODUCT,
})

export const setUpgradesOfDraft = (
  upgrades: any,
): SetUpgradesOfDraftAction => ({
  payload: upgrades,
  type: SET_UPGRADES,
})

export const setDraftStatus = (
  status: SetDraftStatusAction['payload'],
): SetDraftStatusAction => ({
  payload: status,
  type: SET_STATUS,
})

export const setDuplicatedJobDraft = (
  id: string,
  draft: Draft,
  product: JobPostingProduct | UpgradedJobPostingProduct,
  status: JobStatus,
  upgrades: any,
): SetDuplicatedJobDraftAction => ({
  payload: { id, draft, product, status, upgrades },
  type: SET_DUPLICATED_JOB_DRAFT,
})
